/** Angular Imports */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    computed,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Button } from '@ntv360/component-pantry';
import { switchMap, take } from 'rxjs';

/** Core Imports */
import { AuthUser } from '@core/services/auth/auth.interface';
import { AuthService } from '@core/services/auth/auth.service';

/**
 * Authentication configuration for OAuth 2.0 flow.
 *
 * @property API_ENDPOINT - BFF endpoint for token exchange
 * @property CLIENT_ID - Auth0 application client ID (public)
 * @property AUDIENCE - Auth0 API audience identifier
 */
const AUTH_CONFIG = {
    API_ENDPOINT: '/api/auth/login',
    CLIENT_ID: 'QYJzGGgCAyz51d2noULXBeIwyQqZhdHP',
    AUDIENCE: 'https://api.ntv360.com',
} as const;

const CAPTIONS = [
    'Authenticating...',
    'Verifying your credentials...',
    'Setting up your session...',
    'Almost there...',
] as const;

@Component({
    selector: 'app-auth-callback',
    imports: [Button],
    templateUrl: './callback.component.html',
    styleUrl: './callback.component.scss',
    standalone: true,
})
export class CallbackComponent implements OnInit, OnDestroy {
    // ==================== Provider Injections ====================

    private readonly _platformId = inject(PLATFORM_ID);
    private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
    private readonly _http: HttpClient = inject(HttpClient);
    private readonly _router: Router = inject(Router);
    private readonly _authService = inject(AuthService);
    private readonly _destroyRef = inject(DestroyRef);

    // ==================== Component Variables ====================

    private readonly VERIFY_FLAG = 'ntv_pending_verification';

    protected caption: string = CAPTIONS[0];
    protected showVerifyEmail = signal(false);
    protected userEmail = signal('');

    /** Remaining cooldown seconds. 0 = no active cooldown — timer hidden. */
    protected secondsLeft = signal(0);

    /** True once a cooldown countdown reaches zero. Drives the resend button visibility. */
    protected timerExpired = signal(false);

    /** True while a cooldown is running. */
    protected timerActive = computed(() => this.secondsLeft() > 0);

    /** MM:SS display string derived from `secondsLeft`. */
    protected timerDisplay = computed(() => {
        const s = this.secondsLeft();
        const m = Math.floor(s / 60)
            .toString()
            .padStart(2, '0');
        const sec = (s % 60).toString().padStart(2, '0');
        return `${m}:${sec}`;
    });

    private _captionIndex = 0;
    private _captionInterval: ReturnType<typeof setInterval> | null = null;
    private _timerInterval: ReturnType<typeof setInterval> | null = null;

    ngOnInit(): void {
        if (!isPlatformBrowser(this._platformId)) return;

        if (sessionStorage.getItem(this.VERIFY_FLAG)) {
            const raw = sessionStorage.getItem('ntv_user_traits');
            if (raw) {
                const traits = JSON.parse(raw) as { data?: { emailAddress?: string } };
                if (traits?.data?.emailAddress) {
                    this.userEmail.set(traits.data.emailAddress);
                }
            }
            this.showVerifyEmail.set(true);
            this.hydrateTimerFromExpiry();
            return;
        }

        this.startCaptionCycle();
        this.handleAuthCallback();
    }

    ngOnDestroy(): void {
        if (this._captionInterval !== null) {
            clearInterval(this._captionInterval);
        }
        this.clearTimer();
    }

    private startCaptionCycle(): void {
        this._captionInterval = setInterval(() => {
            this._captionIndex = (this._captionIndex + 1) % CAPTIONS.length;
            this.caption = CAPTIONS[this._captionIndex];
        }, 1800);
    }

    /**
     * Handles the OAuth callback by processing query parameters.
     * Extracts authorization code and state, validates them, and initiates token exchange.
     * @returns {void}
     */
    private handleAuthCallback(): void {
        // Get URL query parameters via  Angular's Activated Route class
        this._activatedRoute.queryParamMap.pipe(take(1)).subscribe({
            next: (params) => {
                // Auth0 surfaces blocked / rate-limited flows by redirecting back
                // here with `error=` + `error_description=`. Send those straight to /block.
                const errorParam = params.get('error');
                const errorDescription = params.get('error_description');
                if (this.isRateLimitedError(errorParam, errorDescription)) {
                    this._router.navigate(['/block'], { replaceUrl: true });
                    return;
                }

                // Code coming from Auth0 redirect
                const code = params.get('code');

                // State coming from Auth0 redirect
                const state = params.get('state');

                // Check availability and handle errors if they don't exist
                if (!code || !state) {
                    this.handleAuthError('missing_params');
                    return;
                }

                // Process Auth0 codes if retrieved successfully
                this.processAuthCode(code, state);
            },
        });
    }

    /**
     * Detects Auth0 rate-limit / blocked error returns from the OAuth callback.
     *
     * @param error - Value of the `?error=` query param.
     * @param description - Value of the `?error_description=` query param.
     * @returns True when the error indicates the user has been throttled or blocked.
     */
    private isRateLimitedError(error: string | null, description: string | null): boolean {
        if (!error) return false;
        const code = error.toLowerCase();
        if (code === 'too_many_attempts' || code === 'too_many_requests') return true;
        if (code === 'mfa_invalid_credentials') return true;
        if (code === 'access_denied' && description) {
            const d = description.toLowerCase();
            if (d.includes('blocked') || d.includes('rate') || d.includes('too many')) return true;
        }
        return false;
    }

    /**
     * Processes the authorization code by validating PKCE verifier and exchanging for tokens.
     *
     * @param code - Authorization code from Auth0
     * @param state - State parameter for CSRF protection
     * @returns {void}
     */
    private processAuthCode(code: string, state: string): void {
        // Grab the code verifier which can be found in the Session storage
        const codeVerifier = this.getCodeVerifierFromSession(state);

        // Handle non existent code verifier
        if (!codeVerifier) {
            this.handleAuthError('missing_verifier');
            return;
        }

        // Proceed with Auth0 code exchange
        this.exchangeCodeForTokens(code, codeVerifier);
    }

    /**
     * Exchanges authorization code for tokens via BFF -> Tier1.
     *
     * @param code - Authorization code from Auth0
     * @param codeVerifier - PKCE code verifier from session storage
     * @returns {void}
     */
    private exchangeCodeForTokens(code: string, codeVerifier: string): void {
        // If a different user is currently authenticated (e.g. admin opening a dealer's
        // verify link), clear their local session before we overwrite it with the new
        // user's tokens, and notify other open tabs via BroadcastChannel.
        const hasExistingSession = !!localStorage.getItem('ntv_user_traits');
        if (hasExistingSession) {
            this._authService.broadcastLogout();
            this._authService.clearAuthentication();
        }
        // Expected payload of Tier1 for Auth0 token exchange
        const payload = {
            code,
            redirectUri: window.location.origin + '/auth',
            codeVerifier,
            audience: AUTH_CONFIG.AUDIENCE,
        };

        // Send payload to BFF then Tier1
        this._http
            .post(AUTH_CONFIG.API_ENDPOINT, payload, {
                withCredentials: true, // Enable sending/receiving cookies
            })
            .subscribe({
                next: (response) => this.handleAuthSuccess(response),
                error: () => this.handleAuthFailure(),
            });
    }

    /**
     * Handles successful authentication response.
     *
     * @param response - Authentication response from backend
     * @returns {void}
     */
    private handleAuthSuccess(response: unknown): void {
        if (isPlatformBrowser(this._platformId)) {
            this._authService.setAuthenticated(response as AuthUser);

            const traits = response as { data?: { emailAddress?: string } };
            if (traits?.data?.emailAddress) {
                this.userEmail.set(traits.data.emailAddress);
            }
        }

        this.cleanupTransactionData();

        this._http.get('/api/auth/me', { withCredentials: true }).subscribe({
            next: () => {
                sessionStorage.removeItem(this.VERIFY_FLAG);
                this._router.navigate(['/']);
            },
            error: () => {
                sessionStorage.setItem(this.VERIFY_FLAG, 'true');
                this.triggerResendVerification();
                this.showVerifyEmail.set(true);
            },
        });
    }

    /**
     * Fires a resend-verification request only when no active backend cooldown is in place.
     *
     * @returns {void}
     *
     * @remarks
     * Checks server-issued ttl first to avoid hitting the rate-limit on page refresh
     * or repeated callback hits. ttl > 0 = active cooldown, surface remaining time in
     * the timer. ttl <= 0 = no cooldown key — resend and start timer from new ttl.
     */
    private triggerResendVerification(): void {
        this._authService
            .getEmailVerificationExpiry<{ data: { ttl: number } }>()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((res) => {
                const ttl = res?.data?.ttl ?? -2;
                if (ttl > 0) {
                    this.startTimer(ttl);
                    return;
                }
                this._authService
                    .resendEmailVerification()
                    .pipe(
                        switchMap(() =>
                            this._authService.getEmailVerificationExpiry<{
                                data: { ttl: number };
                            }>(),
                        ),
                        takeUntilDestroyed(this._destroyRef),
                    )
                    .subscribe((expiry) => {
                        const nextTtl = expiry?.data?.ttl ?? 0;
                        if (nextTtl > 0) this.startTimer(nextTtl);
                    });
            });
    }

    /**
     * Pulls the server-issued resend cooldown ttl and starts the countdown if non-zero.
     *
     * @returns {void}
     *
     * @remarks
     * Used on page refresh while the verify-email card is showing — restores the
     * countdown from backend state so the timer survives reloads.
     */
    private hydrateTimerFromExpiry(): void {
        this._authService
            .getEmailVerificationExpiry<{ data: { ttl: number } }>()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((res) => {
                const ttl = res?.data?.ttl ?? 0;
                if (ttl > 0) {
                    this.startTimer(ttl);
                } else {
                    // No active cooldown — surface the resend button immediately.
                    this.timerExpired.set(true);
                }
            });
    }

    /**
     * Starts (or restarts) the resend cooldown countdown.
     *
     * @param seconds - Cooldown seconds to count down from.
     * @returns {void}
     */
    private startTimer(seconds: number): void {
        this.clearTimer();
        this.timerExpired.set(false);
        this.secondsLeft.set(seconds);
        this._timerInterval = setInterval(() => {
            const next = this.secondsLeft() - 1;
            if (next <= 0) {
                this.secondsLeft.set(0);
                this.timerExpired.set(true);
                this.clearTimer();
            } else {
                this.secondsLeft.set(next);
            }
        }, 1000);
    }

    /**
     * User-initiated resend after the cooldown has elapsed. Issues a new
     * verification email and restarts the countdown from the server-issued ttl.
     *
     * @returns {void}
     */
    public resendEmail(): void {
        this._authService
            .resendEmailVerification()
            .pipe(
                switchMap(() =>
                    this._authService.getEmailVerificationExpiry<{ data: { ttl: number } }>(),
                ),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe((res) => {
                const ttl = res?.data?.ttl ?? 0;
                if (ttl > 0) this.startTimer(ttl);
            });
    }

    /**
     * Cancels the active countdown interval if one is running.
     *
     * @returns {void}
     */
    private clearTimer(): void {
        if (this._timerInterval !== null) {
            clearInterval(this._timerInterval);
            this._timerInterval = null;
        }
    }

    /**
     * Handles authentication failure.
     *
     * @returns {void}
     */
    private handleAuthFailure(): void {
        this.cleanupTransactionData();
        this.handleAuthError('auth_failed');
    }

    /**
     * Redirects to login page with error parameter.
     *
     * @param errorType - Type of authentication error
     * @returns {void}
     */
    private handleAuthError(errorType: string): void {
        this._router.navigate(['/login'], {
            queryParams: { error: errorType },
        });
    }

    /**
     * Retrieves and validates the PKCE code verifier from session storage.
     *
     * @param state - OAuth state parameter for CSRF validation
     * @returns The code verifier if found and valid, null otherwise
     *
     * @remarks
     * This method accesses Auth0 SDK's internal storage to retrieve the PKCE
     * code verifier. It also performs CSRF protection by validating the state parameter.
     */
    private getCodeVerifierFromSession(state: string): string | null {
        if (!isPlatformBrowser(this._platformId)) {
            return null;
        }
        const storageKey = `a0.spajs.txs.${AUTH_CONFIG.CLIENT_ID}`;

        // Try to get from sessionStorage (default for auth0-spa-js transactions)
        let transactionData = sessionStorage.getItem(storageKey);

        // Fallback to localStorage just in case
        if (!transactionData) {
            transactionData = localStorage.getItem(storageKey);
        }

        if (!transactionData) {
            return null;
        }

        try {
            const data = JSON.parse(transactionData);

            if (data.state !== state) {
                return null;
            }

            return data.code_verifier || null;
        } catch {
            return null;
        }
    }

    /**
     * Removes Auth0 transaction data from session storage.
     *
     * @returns {void}
     *
     * @remarks
     * Should be called after successful token exchange or on authentication error
     * to prevent replay attacks and clean up sensitive data.
     */
    private cleanupTransactionData(): void {
        if (!isPlatformBrowser(this._platformId)) {
            return;
        }
        const storageKey = `a0.spajs.txs.${AUTH_CONFIG.CLIENT_ID}`;
        sessionStorage.removeItem(storageKey);
    }
}
